import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { NavLink, useNavigate } from 'react-router-dom';
import { registerUser } from "../../../../services/auth";

import { setToken } from "../../../../utils/functions";
import PasswordIcon from '@mui/icons-material/Password';

import { vestResolver } from "@hookform/resolvers/vest";
import { grey } from "@mui/material/colors";
import validators from "./validators";

import DashedBreakline from "../../../../components/Shared/DashedBreakline";

export default function RegisterIndividualForm({ confirmedType, setConfirmedType }: any) {
  const navigate = useNavigate()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      password_confirmation: "",
      type: confirmedType
    },
    resolver: vestResolver(validators)
  });

  const [type, setType] = useState<string>('password')
  const [errorMessage, setErrorMessage] = useState<string | null>("");

  const { mutateAsync, isLoading } = useMutation(registerUser);

  const handleRegisterUser = async (data: any) => {
    setErrorMessage(null);

    data.type = confirmedType

    const response = await mutateAsync(data)
    if (response?.errors) return setErrorMessage(response?.errors?.[0]?.message);

    const { access_token, exp, uuid } = response?.data || {};

    if (access_token && exp && uuid) {
      return [
        setToken(access_token, exp, uuid),
        navigate("/")
      ]
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleRegisterUser)}>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 4, fontSize: 14 }}>{errorMessage}</Alert>
      )}

      <Controller
        name="email"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            type="text"
            label="Email (*)"
            error={!!errors.email}
            InputLabelProps={{ shrink: true }}
            inputProps={{ autoComplete: 'new-password' }}
            helperText={errors.email?.message}
            sx={{ minWidth: "100%", "& input": { fontSize: 13 } }}
          />
        )}
      />

      <Stack direction="row" sx={{ position: 'relative' }}>
        <Controller
          name="password"
          defaultValue=""
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size="small"
              type={type}
              label="Password (*)"
              error={!!errors?.password}
              InputLabelProps={{ shrink: true }}
              helperText={errors?.password?.message}
              sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
            />
          )}
        />
        <IconButton aria-label="show" sx={{ position: 'absolute', top: 24, right: 0 }} onClick={() => setType(type === 'text' ? 'password' : 'text')}>
          <PasswordIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Controller
        name="password_confirmation"
        defaultValue=""
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            type={type}
            label="Password Confirmation (*)"
            error={!!errors?.password_confirmation}
            InputLabelProps={{ shrink: true }}
            helperText={errors?.password_confirmation?.message}
            sx={{ minWidth: "100%", "& input": { fontSize: 13 }, mt: 3 }}
          />
        )}
      />

      <Button
        disabled={isLoading}
        type="submit"
        sx={{ mb: 1, mt: 3 }}
        fullWidth
        variant="contained"
      >
        {isLoading ? (
          <CircularProgress size={24} sx={{ color: grey[900] }} />
        ) : (
          <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>Create Account</Typography>
        )}
      </Button>

      <Button fullWidth sx={{ bgcolor: grey[50], color: 'black ! important' }} onClick={() => setConfirmedType(null)}>
        <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>
          Back
        </Typography>
      </Button>

      <DashedBreakline text="Already have account?" />

      <Button component={NavLink} to="/auth/login" fullWidth sx={{ bgcolor: grey[50], color: 'black ! important' }}>
        <Typography fontSize={14} fontWeight={700} sx={{ textTransform: 'capitalize' }}>
          Customer Login
        </Typography>
      </Button>
    </Box>
  );
}
